/**
 * UI text in English, Hindi and Marathi. Keys are English-ish ids; {name} placeholders are
 * filled by t(). Clinical content (diagnoses, medicines, reports) stays as the doctor wrote it.
 */
export type Lang = 'en' | 'hi' | 'mr';

export const LANGS: { code: Lang; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिन्दी' },
  { code: 'mr', label: 'मराठी' },
];

type Dict = Record<string, string>;

const en: Dict = {
  // navigation
  'nav.dashboard': 'Dashboard',
  'nav.appointments': 'Appointments',
  'nav.scheduling': 'Scheduling',
  'nav.billing': 'Billing',
  'nav.pharmacy': 'Pharmacy',
  'nav.waitingRoom': 'Waiting Room Screen',
  'nav.patients': 'Patients',
  'nav.vaccinations': 'Vaccinations',
  'nav.prescriptions': 'Prescriptions',
  'nav.labReports': 'Lab Reports',
  'nav.myAvailability': 'My Availability',
  'nav.medicines': 'Medicine Database',
  'nav.staff': 'Staff',
  'nav.hospitalSettings': 'Hospital Settings',
  'nav.branches': 'Branches',
  'nav.apiKeys': 'API Keys',
  'nav.orgMetrics': 'Organization Metrics',
  'nav.auditLog': 'Audit Log',
  'nav.newReport': 'New Report',
  'nav.dicom': 'DICOM Studies',
  // top bar and help
  'top.search': 'Search',
  'help.title': 'Help & Support',
  'help.guide': 'Quick guide',
  'help.guideSub': 'How to do common tasks',
  'help.report': 'Report a problem',
  'help.reportSub': 'Tell the MedEase team what went wrong',
  'help.whatsapp': 'WhatsApp support',
  'help.whatsappSub': 'Chat with the MedEase team',
  'menu.profile': 'Profile',
  'menu.settings': 'Settings',
  'menu.logout': 'Logout',
  'menu.darkMode': 'Dark mode',
  // settings
  'settings.language': 'Language',
  'settings.languageHelp': 'Menus, buttons and patient pages. Clinical notes stay as written.',
  'settings.appearance': 'Appearance',
  'settings.theme': 'Theme',
  'settings.themeSystem': 'Same as this device',
  'settings.themeLight': 'Light',
  'settings.themeDark': 'Dark',
  'settings.savedOnDevice': 'Saved on this device.',
  // public booking
  'book.unavailable': 'This booking page is not available. Please contact the clinic directly.',
  'book.chooseDoctor': '1. Choose a doctor',
  'book.pickTime': '2. Pick a day and time',
  'book.yourDetails': '3. Your details',
  'book.noTimes': '{doctor} has no free times on this day. Try another day.',
  'book.soon': 'Online booking will open soon. Please call the clinic to book.',
  'book.name': "Patient's full name",
  'book.mobile': 'Mobile number',
  'book.mobileHelp': 'The clinic may call you on this number',
  'book.mobileError': 'Enter a 10-digit mobile number',
  'book.age': 'Age',
  'book.gender': 'Gender',
  'book.female': 'Female',
  'book.male': 'Male',
  'book.other': 'Other',
  'book.reason': 'Reason for visit (optional)',
  'book.consent': 'I agree to {clinic} using my details as described in the',
  'book.notice': 'privacy notice',
  'book.button': 'Book {date} at {time}',
  'book.booking': 'Booking…',
  'book.done': 'Appointment booked',
  'book.arrive': 'Please arrive 10 minutes early. To change or cancel, call the clinic{phone}.',
  'book.failed': 'Could not book right now. Please call the clinic.',
  // patient records
  'portal.expired': 'This link has expired. Ask the clinic to send a new one.',
  'portal.records': 'Your health records',
  'portal.unlockYear': 'To keep them private, enter the year you were born.',
  'portal.unlockMobile': 'To keep them private, enter the last 4 digits of your mobile number.',
  'portal.year': 'Year of birth',
  'portal.mobile4': 'Last 4 digits of mobile',
  'portal.open': 'Open',
  'portal.checking': 'Checking…',
  'portal.hello': 'Namaste, {name}',
  'portal.install': 'Add to your home screen to open your records like an app.',
  'portal.add': 'Add',
  'portal.upcoming': 'Upcoming appointments',
  'portal.noneBooked': 'None booked.',
  'portal.book': 'Book an appointment',
  'portal.prescriptions': 'Prescriptions',
  'portal.noPrescriptions': 'No prescriptions yet.',
  'portal.medicines': 'Medicines: {n}',
  'portal.labs': 'Lab reports',
  'portal.noLabs': 'No reports yet.',
  'portal.lock': 'Lock on this phone',
};

const hi: Dict = {
  'nav.dashboard': 'डैशबोर्ड',
  'nav.appointments': 'अपॉइंटमेंट',
  'nav.scheduling': 'शेड्यूल',
  'nav.billing': 'बिलिंग',
  'nav.pharmacy': 'फ़ार्मेसी',
  'nav.waitingRoom': 'प्रतीक्षा कक्ष स्क्रीन',
  'nav.patients': 'मरीज़',
  'nav.vaccinations': 'टीकाकरण',
  'nav.prescriptions': 'प्रिस्क्रिप्शन',
  'nav.labReports': 'लैब रिपोर्ट',
  'nav.myAvailability': 'मेरा समय',
  'nav.medicines': 'दवाइयों की सूची',
  'nav.staff': 'स्टाफ़',
  'nav.hospitalSettings': 'अस्पताल सेटिंग्स',
  'nav.branches': 'शाखाएँ',
  'nav.apiKeys': 'API कुंजियाँ',
  'nav.orgMetrics': 'आँकड़े',
  'nav.auditLog': 'ऑडिट लॉग',
  'nav.newReport': 'नई रिपोर्ट',
  'nav.dicom': 'DICOM स्टडी',
  'top.search': 'खोजें',
  'help.title': 'मदद',
  'help.guide': 'छोटी गाइड',
  'help.guideSub': 'रोज़ के काम कैसे करें',
  'help.report': 'समस्या बताएँ',
  'help.reportSub': 'MedEase टीम को बताएँ क्या गड़बड़ हुई',
  'help.whatsapp': 'WhatsApp सहायता',
  'help.whatsappSub': 'MedEase टीम से बात करें',
  'menu.profile': 'प्रोफ़ाइल',
  'menu.settings': 'सेटिंग्स',
  'menu.logout': 'लॉग आउट',
  'menu.darkMode': 'डार्क मोड',
  'settings.language': 'भाषा',
  'settings.languageHelp': 'मेनू, बटन और मरीज़ों के पेज। क्लिनिकल नोट्स जैसे लिखे गए वैसे ही रहते हैं।',
  'settings.appearance': 'दिखावट',
  'settings.theme': 'थीम',
  'settings.themeSystem': 'इस डिवाइस जैसा',
  'settings.themeLight': 'हल्का',
  'settings.themeDark': 'गहरा',
  'settings.savedOnDevice': 'इस डिवाइस पर सहेजा गया।',
  'book.unavailable': 'यह बुकिंग पेज उपलब्ध नहीं है। कृपया सीधे क्लिनिक से संपर्क करें।',
  'book.chooseDoctor': '1. डॉक्टर चुनें',
  'book.pickTime': '2. दिन और समय चुनें',
  'book.yourDetails': '3. आपकी जानकारी',
  'book.noTimes': 'इस दिन {doctor} का कोई समय खाली नहीं है। कोई और दिन चुनें।',
  'book.soon': 'ऑनलाइन बुकिंग जल्द शुरू होगी। बुक करने के लिए क्लिनिक को कॉल करें।',
  'book.name': 'मरीज़ का पूरा नाम',
  'book.mobile': 'मोबाइल नंबर',
  'book.mobileHelp': 'क्लिनिक इस नंबर पर आपको कॉल कर सकता है',
  'book.mobileError': '10 अंकों का मोबाइल नंबर डालें',
  'book.age': 'उम्र',
  'book.gender': 'लिंग',
  'book.female': 'महिला',
  'book.male': 'पुरुष',
  'book.other': 'अन्य',
  'book.reason': 'आने का कारण (वैकल्पिक)',
  'book.consent': 'मैं सहमत हूँ कि {clinic} मेरी जानकारी का उपयोग इसके अनुसार करे:',
  'book.notice': 'गोपनीयता सूचना',
  'book.button': '{date} को {time} बजे बुक करें',
  'book.booking': 'बुक हो रहा है…',
  'book.done': 'अपॉइंटमेंट बुक हो गया',
  'book.arrive': 'कृपया 10 मिनट पहले पहुँचें। बदलने या रद्द करने के लिए क्लिनिक को कॉल करें{phone}।',
  'book.failed': 'अभी बुक नहीं हो सका। कृपया क्लिनिक को कॉल करें।',
  'portal.expired': 'यह लिंक समाप्त हो गया है। क्लिनिक से नया लिंक भेजने को कहें।',
  'portal.records': 'आपके स्वास्थ्य रिकॉर्ड',
  'portal.unlockYear': 'इन्हें निजी रखने के लिए अपना जन्म वर्ष डालें।',
  'portal.unlockMobile': 'इन्हें निजी रखने के लिए अपने मोबाइल नंबर के आखिरी 4 अंक डालें।',
  'portal.year': 'जन्म वर्ष',
  'portal.mobile4': 'मोबाइल के आखिरी 4 अंक',
  'portal.open': 'खोलें',
  'portal.checking': 'जाँच हो रही है…',
  'portal.hello': 'नमस्ते, {name}',
  'portal.install': 'अपने रिकॉर्ड ऐप की तरह खोलने के लिए इसे होम स्क्रीन पर जोड़ें।',
  'portal.add': 'जोड़ें',
  'portal.upcoming': 'आने वाले अपॉइंटमेंट',
  'portal.noneBooked': 'कोई बुक नहीं है।',
  'portal.book': 'अपॉइंटमेंट बुक करें',
  'portal.prescriptions': 'प्रिस्क्रिप्शन',
  'portal.noPrescriptions': 'अभी कोई प्रिस्क्रिप्शन नहीं है।',
  'portal.medicines': 'दवाइयाँ: {n}',
  'portal.labs': 'लैब रिपोर्ट',
  'portal.noLabs': 'अभी कोई रिपोर्ट नहीं है।',
  'portal.lock': 'इस फ़ोन पर लॉक करें',
};

const mr: Dict = {
  'nav.dashboard': 'डॅशबोर्ड',
  'nav.appointments': 'अपॉइंटमेंट्स',
  'nav.scheduling': 'वेळापत्रक',
  'nav.billing': 'बिलिंग',
  'nav.pharmacy': 'फार्मसी',
  'nav.waitingRoom': 'प्रतीक्षा कक्ष स्क्रीन',
  'nav.patients': 'रुग्ण',
  'nav.vaccinations': 'लसीकरण',
  'nav.prescriptions': 'प्रिस्क्रिप्शन',
  'nav.labReports': 'लॅब रिपोर्ट',
  'nav.myAvailability': 'माझी वेळ',
  'nav.medicines': 'औषधांची यादी',
  'nav.staff': 'कर्मचारी',
  'nav.hospitalSettings': 'रुग्णालय सेटिंग्ज',
  'nav.branches': 'शाखा',
  'nav.apiKeys': 'API की',
  'nav.orgMetrics': 'आकडेवारी',
  'nav.auditLog': 'ऑडिट लॉग',
  'nav.newReport': 'नवीन रिपोर्ट',
  'nav.dicom': 'DICOM स्टडी',
  'top.search': 'शोधा',
  'help.title': 'मदत',
  'help.guide': 'छोटी मार्गदर्शिका',
  'help.guideSub': 'रोजची कामे कशी करायची',
  'help.report': 'समस्या कळवा',
  'help.reportSub': 'MedEase टीमला काय चुकले ते सांगा',
  'help.whatsapp': 'WhatsApp मदत',
  'help.whatsappSub': 'MedEase टीमशी बोला',
  'menu.profile': 'प्रोफाइल',
  'menu.settings': 'सेटिंग्ज',
  'menu.logout': 'लॉग आउट',
  'menu.darkMode': 'डार्क मोड',
  'settings.language': 'भाषा',
  'settings.languageHelp': 'मेनू, बटणे आणि रुग्णांची पाने. क्लिनिकल नोंदी लिहिल्याप्रमाणेच राहतात.',
  'settings.appearance': 'दिसणे',
  'settings.theme': 'थीम',
  'settings.themeSystem': 'या डिव्हाइसप्रमाणे',
  'settings.themeLight': 'फिकट',
  'settings.themeDark': 'गडद',
  'settings.savedOnDevice': 'या डिव्हाइसवर जतन केले.',
  'book.unavailable': 'हे बुकिंग पेज उपलब्ध नाही. कृपया थेट क्लिनिकशी संपर्क साधा.',
  'book.chooseDoctor': '1. डॉक्टर निवडा',
  'book.pickTime': '2. दिवस आणि वेळ निवडा',
  'book.yourDetails': '3. तुमची माहिती',
  'book.noTimes': 'या दिवशी {doctor} यांची वेळ उपलब्ध नाही. दुसरा दिवस निवडा.',
  'book.soon': 'ऑनलाइन बुकिंग लवकरच सुरू होईल. बुकिंगसाठी क्लिनिकला कॉल करा.',
  'book.name': 'रुग्णाचे पूर्ण नाव',
  'book.mobile': 'मोबाईल नंबर',
  'book.mobileHelp': 'क्लिनिक या नंबरवर तुम्हाला कॉल करू शकते',
  'book.mobileError': '10 अंकी मोबाईल नंबर टाका',
  'book.age': 'वय',
  'book.gender': 'लिंग',
  'book.female': 'स्त्री',
  'book.male': 'पुरुष',
  'book.other': 'इतर',
  'book.reason': 'भेटीचे कारण (ऐच्छिक)',
  'book.consent': '{clinic} ने माझी माहिती याप्रमाणे वापरण्यास माझी संमती आहे:',
  'book.notice': 'गोपनीयता सूचना',
  'book.button': '{date} रोजी {time} वाजता बुक करा',
  'book.booking': 'बुक होत आहे…',
  'book.done': 'अपॉइंटमेंट बुक झाली',
  'book.arrive': 'कृपया 10 मिनिटे आधी या. बदल किंवा रद्द करण्यासाठी क्लिनिकला कॉल करा{phone}.',
  'book.failed': 'आत्ता बुक करता आले नाही. कृपया क्लिनिकला कॉल करा.',
  'portal.expired': 'ही लिंक कालबाह्य झाली आहे. क्लिनिकला नवीन लिंक पाठवण्यास सांगा.',
  'portal.records': 'तुमचे आरोग्य रेकॉर्ड',
  'portal.unlockYear': 'ते खाजगी ठेवण्यासाठी तुमचे जन्मवर्ष टाका.',
  'portal.unlockMobile': 'ते खाजगी ठेवण्यासाठी तुमच्या मोबाईल नंबरचे शेवटचे 4 अंक टाका.',
  'portal.year': 'जन्मवर्ष',
  'portal.mobile4': 'मोबाईलचे शेवटचे 4 अंक',
  'portal.open': 'उघडा',
  'portal.checking': 'तपासत आहे…',
  'portal.hello': 'नमस्कार, {name}',
  'portal.install': 'तुमचे रेकॉर्ड ॲपसारखे उघडण्यासाठी हे होम स्क्रीनवर जोडा.',
  'portal.add': 'जोडा',
  'portal.upcoming': 'आगामी अपॉइंटमेंट्स',
  'portal.noneBooked': 'काहीही बुक नाही.',
  'portal.book': 'अपॉइंटमेंट बुक करा',
  'portal.prescriptions': 'प्रिस्क्रिप्शन',
  'portal.noPrescriptions': 'अजून कोणतेही प्रिस्क्रिप्शन नाही.',
  'portal.medicines': 'औषधे: {n}',
  'portal.labs': 'लॅब रिपोर्ट',
  'portal.noLabs': 'अजून कोणताही रिपोर्ट नाही.',
  'portal.lock': 'या फोनवर लॉक करा',
};

export const STRINGS: Record<Lang, Dict> = { en, hi, mr };

/** Looks up a key (falling back to English, then the key) and fills {placeholders}. */
export const translate = (lang: Lang, key: string, vars?: Record<string, string | number>) => {
  const text = STRINGS[lang]?.[key] ?? en[key] ?? key;
  return vars ? text.replace(/\{(\w+)\}/g, (_, k) => (vars[k] ?? '').toString()) : text;
};
